(ns app.front.view.bim.upload
  (:require [ajax.core :as ajax :refer [PUT]]
            [app.domain.attachment.file-formats :as file-formats]
            [app.domain.bim.attachment-model :as bim-attachment-model]
            [app.front.file.upload :as upload]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.ui.text-button :as tb]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.attachment.upload :as att-upload]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

;; Helper functions
;; ================

(def file-element-id "bim-file-upload-input")

(defn file-version-element-id [att-id]
  (str file-element-id att-id))

(def ifc-content-type "application/x-step") ;; browser does not recognize the type
(def max-size 1073741824) ;; 1 GiB

(defn process-file-read [target-id attachment-id file event]
  (let [md5-base64 (-> event .-target .-result js/Uint8Array. upload/md5)]
    (>evt [::file-added target-id (p/assoc-if {:filename        (.-name file)
                                               :size            (.-size file)
                                               :content-type    ifc-content-type
                                               :md5-base64      md5-base64
                                               :file            file
                                               :upload-progress 0
                                               :file-id         nil}
                                              :attachment-id attachment-id)])))

(defn valid-file? [f]
  (and (-> (.-name f) (str/ends-with? file-formats/ifc-file-extension))
       (< (.-size f) max-size)))

(defn read-file [f on-load]
  (let [reader (js/FileReader.)]
    (set! (.-onload reader) on-load)
    (.readAsArrayBuffer reader f)))

(defn read-files [target-id files]
  (>evt [::set-file-read-in-progress true])
  ;; This reads all the files completely to memory at the same time, might have to consider reading in chunks
  (doseq [f files]
    (if (valid-file? f)
      (read-file f (partial process-file-read target-id nil f))
      (>evt [::set-file-read-in-progress false]))))

(defn handle-files [target-id _]
  (let [files (->> (.getElementById js/document file-element-id)
                   .-files
                   array-seq)]
    (read-files target-id files)))

(defn handle-file-version-upload [target-id att-id _]
  (p/if-all-let [elem (.getElementById js/document (file-version-element-id att-id))
                 file (->> elem
                           .-files
                           array-seq
                           first)]
    (when (valid-file? file)
      (>evt [::version-add-started att-id])
      (set! (.-value elem) "") ;; Have to reset the input value so that next change is detected properly
      (read-file file (partial process-file-read target-id att-id file)))))

(defn stop-event [e]
  (.stopPropagation e)
  (.preventDefault e))

(defn- handle-file-drop [target-id e]
  (stop-event e)
  (p/if-all-let [dt    (.-dataTransfer e)
                 files (-> (.-files dt)
                           array-seq)]
    (read-files target-id files)))

(defn drag-over [e]
  (stop-event e)
  (-> e .-dataTransfer .-dropEffect (set! "copy")))

(defn save-attachments [uploads target-id]
  (doseq [[_ up] uploads]
    (>evt [::create-attachment target-id up])))

;; Subs
;; ====


(rf/reg-sub ::pending-uploads :-> ::pending-uploads)

(rf/reg-sub ::pending-creates?
  (fn [db]
    (-> db ::pending-creates seq boolean)))

(rf/reg-sub ::all-uploads-valid?
  :<- [::pending-uploads]
  (fn [uploads]
    (->> (vals uploads)
         (every? (every-pred :file-id :bim-attachment/type)))))

(rf/reg-sub ::file-read-in-progress :-> ::file-read-in-progress)

(rf/reg-sub ::upload-metadata-input-active
  :<- [::pending-uploads]
  :-> seq)

;; Event handlers
;; ==============

(rf/reg-event-fx ::start-file-upload
  (fn [{:keys [db]}
       [_ target-id {:keys [md5-base64 content-type file attachment-id] :as file-metadata}
        _
        {:keys [file-id upload-url]}]]
    (let [ajax-req (PUT upload-url {:headers          {"Content-Type" content-type}
                                    :body             file
                                    :handler          #(>evt
                                                         (if attachment-id
                                                           [::version-upload-complete target-id file-metadata file-id]
                                                           [::upload-complete target-id md5-base64 file-id]))
                                    :progress-handler #(>evt
                                                         (if attachment-id
                                                           [::update-version-progress attachment-id %]
                                                           [::update-progress md5-base64 %]))
                                    :error-handler    #(>evt [::upload-failure file-metadata %])})]
      (when-not attachment-id
        {:db (assoc-in db [::pending-uploads md5-base64 :ajax-request] ajax-req)}))))

(rf/reg-event-fx ::update-progress
  (fn [{:keys [db]} [_ md5-base64 progress-event]]
    (p/if-all-let [pending   (get-in db [::pending-uploads md5-base64])
                   new-value (att-upload/calculate-progress (:upload-progress pending) progress-event)]
      {:db (assoc-in db [::pending-uploads md5-base64 :upload-progress] new-value)})))

(rf/reg-event-fx ::update-version-progress
  (fn [{:keys [db]} [_ attachment-id progress-event]]
    (p/if-all-let [current-value (get-in db [::pending-version-uploads attachment-id :upload-progress] 0)
                   new-value     (att-upload/calculate-progress current-value progress-event)]
      {:db (assoc-in db [::pending-version-uploads attachment-id :upload-progress] new-value)})))

(rf/reg-event-fx ::upload-complete
  (fn [{:keys [db]} [_ _ md5-base64 file-id]]
    (if (get-in db [::pending-uploads md5-base64])
      {:db (-> db
               (assoc-in [::pending-uploads md5-base64 :file-id] file-id)
               (assoc-in [::pending-uploads md5-base64 :upload-progress] 100))}
      {:db db})))

(def upload-fail-notification [:notifications/add :toast :error :error/file-upload-title :error/file-upload-caption])

(defn log-upload-error [status-or-error server-error]
  (if server-error
    (js/console.error "File upload initialization failure:" status-or-error (pr-str server-error))
    (js/console.error "File upload storage PUT failure:" (pr-str status-or-error))))

(rf/reg-event-fx ::upload-failure
  (fn [{:keys [db]} [_ {:keys [md5-base64 attachment-id]} status-or-error server-error]]
    (log-upload-error status-or-error server-error)
    {:db       (if attachment-id
                 (p/dissoc-in db [::pending-version-uploads attachment-id])
                 (assoc-in db [::pending-uploads md5-base64 :error] true))
     :dispatch upload-fail-notification}))

(rf/reg-event-fx ::version-upload-complete
  (fn [_ [_ target-id {:keys [attachment-id] :as file-metadata} file-id]]
    {:dispatch [::process/signal
                target-id
                [:process.signal/bim-attachment-add-version
                 (-> (select-keys file-metadata [:content-type :size :filename])
                     (merge (util/mmap attachment-id file-id))
                     (set/rename-keys {:content-type :bim-attachment-version/content-type
                                       :filename     :bim-attachment-version/filename
                                       :size         :bim-attachment-version/size}))]
                {:on-done [::version-add-done attachment-id]}]}))

(rf/reg-event-db ::version-add-done
  (fn [db [_ att-id]]
    (p/dissoc-in db [::pending-version-uploads att-id])))

(rf/reg-event-db ::version-add-started
  (fn [db [_ att-id]]
    (assoc-in db [::pending-version-uploads att-id] {:attachment-id   att-id
                                                     :upload-progress 0})))

(rf/reg-sub ::pending-version-uploads :-> ::pending-version-uploads)

(rf/reg-sub ::version-metadata-for-attachment
  :<- [::pending-version-uploads]
  (fn [pending-uploads [_ att-id]]
    (get pending-uploads att-id)))

(rf/reg-event-fx ::file-added
  (fn [{:keys [db]} [_ target-id {:keys [content-type md5-base64 attachment-id] :as file-metadata}]]
    {:db           (-> (if attachment-id
                         (assoc-in db [::pending-version-uploads attachment-id] file-metadata)
                         (assoc-in db [::pending-uploads md5-base64] file-metadata))
                       (assoc ::file-read-in-progress false))
     :comm/command {:command         :attachment/initialize-upload
                    :data            {:content-type content-type
                                      :md5-digest   md5-base64}
                    :on-success      [::start-file-upload target-id file-metadata]
                    :on-server-error [::upload-failure file-metadata]}}))

(rf/reg-event-fx ::create-attachment
  (fn [{:keys [db]} [_ target-id attachment]]
    {:db       (update db ::pending-creates conj (:md5-base64 attachment))
     :dispatch [::process/signal
                target-id
                [:process.signal/bim-attachment-create
                 (-> (select-keys attachment [:file-id
                                              :content-type
                                              :size
                                              :filename
                                              :bim-attachment/type
                                              :bim-attachment/description])
                     (set/rename-keys {:content-type :bim-attachment-version/content-type
                                       :filename     :bim-attachment-version/filename
                                       :size         :bim-attachment-version/size}))]
                {:on-success [::create-attachment.success (:md5-base64 attachment)]
                 :on-done    [::create-attachment.done (:md5-base64 attachment)]
                 :on-error   [::create-attachment.done (:md5-base64 attachment)]}]}))

(rf/reg-event-db ::create-attachment.success
  (fn [db [_ md5 _]]
    (p/dissoc-in db [::pending-uploads md5])))

(rf/reg-event-db ::create-attachment.done
  (fn [db [_ md5]]
    (update db ::pending-creates (fn [c] (remove #(= md5 %) c)))))

(rf/reg-event-db ::data-entered
  (fn [db [_ md5-base64 field value]]
    (-> db
        (assoc-in [::pending-uploads md5-base64 field] value))))

(rf/reg-event-db ::set-file-read-in-progress
  (fn [db [_ in-progress?]]
    (assoc db ::file-read-in-progress in-progress?)))

(rf/reg-event-db ::cancel-upload
  (fn [db [_ maybe-md5]]
    (doseq [upload (if maybe-md5
                     [(get-in db [::pending-uploads maybe-md5])]
                     (-> db ::pending-uploads vals))]
      (when-let [req (:ajax-request upload)]
        (ajax/abort req)))
    (if maybe-md5
      (p/dissoc-in db [::pending-uploads maybe-md5])
      (assoc db ::pending-uploads {}))))

;; Reagent components
;; ==================

(defn progress-bar [amount]
  [:div.file-progress-bar.border.border-solid
   [:div.progress-color {:style {:width (str amount "%")}}]
   [:div.progress-text {:style {:color (if (< amount 50) "black" "white")}}
    (str amount " %")]])

(defn error-bar [text]
  [:div.file-progress-bar.bg-ui-red-110
   [:div.progress-text.text-ui-white
    text]])

(defn metadata-text-input [{:keys [tr md5 attr file-data]}]
  [ui/text-input {:id         (str md5 (name attr))
                  :label-text (tr [attr])
                  :light      true
                  :value      (attr file-data)
                  :on-change  #(rf/dispatch-sync [::data-entered md5 attr (futil/tval %)])}])

(defn attachment-type-select [{:keys [tr file-data md5]}]
  [ui-select/select
   {:id           (str md5 "-bim-attachment-type-select")
    :class-name   "full-width-select"
    :label-text   (tr [:bim-attachment/type])
    :light        true
    :allow-empty? false
    :values       bim-attachment-model/types
    :on-change    #(>evt [::data-entered md5 :bim-attachment/type %])
    :value        (:bim-attachment/type file-data)
    :val->str     #(tr [%])}])

(defn attachments-list [tr]
  (let [pending-files (<sub [::pending-uploads])]
    (when (seq pending-files)
      (for-frag [[md5
                  {:keys [filename
                          upload-progress
                          size
                          error]
                   :as   file-data}] pending-files]
        ^{:key md5}
        [:div.bg-ui-light-gray.p-4.mb-4
         [:div.flex.justify-between.w-full.items-center
          [:div.flex-1
           (str filename " (" (att-upload/format-size size) ")")]
          [:div.w-32
           (if error
             [error-bar (tr [:error/error])]
             [progress-bar upload-progress])]
          [:span.controls.w-8
           (cc/icon {:icon        i/trash
                     :description (tr [:upload/remove-attachment])
                     :class-name  "cursor-pointer"
                     :on-click    #(>evt [::cancel-upload md5])})]]
         [:<>
          [:div.flex.flex-row.flex-wrap.w-full.gap-4
           [:div.w-full.md:w-1-3
            [attachment-type-select (util/mmap tr md5 file-data)]]
           [:div.flex-1
            [metadata-text-input
             (util/mmap {:attr :bim-attachment/description}
                        tr md5 file-data)]]]]]))))

(defn initial-attachment-metadata-input [{:keys [target-id]}]
  (let [tr (<sub [:tr])]
    [:div.upload
     [attachments-list tr]
     [:div.buttons
      (cc/button
        {:kind             :primary
         :icon             i/check
         :icon-description ""
         :disabled         (or (not (<sub [::all-uploads-valid?]))
                               (<sub [::pending-creates?]))
         :on-click         #(save-attachments (<sub [::pending-uploads]) target-id)}
        (tr [:upload/done]))
      (cc/button
        {:kind             :tertiary
         :icon             i/remove
         :icon-description ""
         :on-click         #(>evt [::cancel-upload])}
        (tr [:cancel]))]]))

(defn upload-widget [{:keys [tr target-id]}]
  (let [read-in-progress? (<sub [::file-read-in-progress])]
    [:div.upload-area__drop-area
     {:on-drag-enter stop-event
      :on-drag-over  drag-over
      :on-drop       (partial handle-file-drop target-id)}
     (if read-in-progress?
       [:div
        (cc/inline-loading {:description (tr [:upload/preparing-new-files])})]
       [:div.leading-tight
        [:input {:type      "file"
                 :id        file-element-id
                 :multiple  "multiple"
                 :style     {:display "none"}
                 :accept    file-formats/ifc-file-extension
                 :on-change (partial handle-files target-id)}]
        [:div.upload-area__drop-area__upper
         [:span (tr [:attachments/upload-text1])]
         [:br]
         [:span
          [tb/text-button-2
           {:on-click #(att-upload/open-filepicker file-element-id %)}
           (tr [:attachments/upload-link])]
          " "
          (tr [:attachments/upload-text2])]]
        [:div.upload-area__drop-area__lower.max-w-xl
         [:span (tr [:bim-section/upload-text])]]])]))

(defn upload-area [tr target]
  (when (<sub [::process/signal-permitted? target [:process.signal/bim-attachment-create]])
    (let [opts {:target-id (if (number? target)
                             target
                             (:db/id target))
                :tr        tr}]
      [:div.upload-area
       (cond
         (<sub [::upload-metadata-input-active])
         [initial-attachment-metadata-input opts]
         :else
         [upload-widget opts])])))

(defn upload-version-button [{:keys [phase attachment-id tr]}]
  (when (<sub [::process/signal-permitted?
               phase
               [:process.signal/bim-attachment-add-version {:id attachment-id}]])
    (let [{:keys [upload-progress] :as version-metadata} (<sub [::version-metadata-for-attachment attachment-id])]
      [:div.file-version-upload
       [:input {:type      "file"
                :id        (file-version-element-id attachment-id)
                :style     {:display "none"}
                :accept    file-formats/ifc-file-extension
                :on-change (partial handle-file-version-upload (:db/id phase) attachment-id)}]
       (if version-metadata
         [:<>
          [:div.mr-4 (tr [:upload/uploading])]
          [progress-bar upload-progress]]
         (cc/button
           {:kind     :tertiary
            :icon     i/upload
            :size     :field
            :on-click #(att-upload/open-filepicker (file-version-element-id attachment-id) %)}
           (tr [:upload/upload-new-version])))])))
